import React, { useState } from "react";
import { Shield, ShieldCheck, ShieldAlert, Star, Users, BedDouble, Building2, Sparkle, Footprints, Info, X, ShoppingBag, Check, Heart, GraduationCap } from "lucide-react";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import SafeImage from "./SafeImage";

function typeIcon(t) {
  if (t === "hostel") return BedDouble;
  if (t === "boutique") return Sparkle;
  return Building2;
}

function safetyColor(s) {
  if (s >= 9) return "text-emerald-400 border-emerald-400/40";
  if (s >= 8) return "text-gold border-gold/40";
  if (s >= 7) return "text-amber-400 border-amber-400/40";
  return "text-accent-red border-accent-red/40";
}

function safetyIcon(s) {
  if (s >= 9) return ShieldCheck;
  if (s >= 7) return Shield;
  return ShieldAlert;
}

function barColor(s) {
  if (s >= 8.5) return "bg-emerald-400";
  if (s >= 7) return "bg-gold";
  if (s >= 5) return "bg-amber-400";
  return "bg-accent-red";
}

export default function AccommodationCard({ acc, index = 0 }) {
  const [open, setOpen] = useState(false);
  const { items, addItem } = useCart();
  const inCart = items.some((x) => x.kind === "accommodation" && x.id === acc.id);
  const Type = typeIcon(acc.type);
  const SafetyIcon = safetyIcon(acc.safety_score);
  const safetyCls = safetyColor(acc.safety_score);
  const visibleTags = (acc.tag_labels || []).slice(0, 4);
  const sv2 = acc.safety_v2;

  return (
    <div
      data-testid={`accommodation-${acc.id}`}
      className="iq-card overflow-hidden grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-0 animate-fade-up hover:border-gold/40 transition-colors"
      style={{ animationDelay: `${index * 40}ms` }}
    >
      <div className="relative aspect-[4/3] sm:aspect-auto sm:h-full bg-ink overflow-hidden">
        <SafeImage src={acc.image} alt={acc.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute top-2 left-2 bg-ink/85 text-cream text-[10px] font-display tracking-widest px-2 py-1 rounded-sm flex items-center gap-1 uppercase">
          <Type className="h-3 w-3 text-gold" /> {acc.type}
        </div>
      </div>
      <div className="p-4 flex flex-col gap-2 min-w-0">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h4 className="font-display text-xl text-cream tracking-wider min-w-0 truncate">{acc.name}</h4>
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center gap-1 text-cream" title="Guest rating">
              <Star className="h-3.5 w-3.5 fill-gold text-gold" />
              <span className="font-display text-sm">{acc.rating.toFixed(1)}</span>
            </div>
            <button
              data-testid={`safety-btn-${acc.id}`}
              onClick={() => setOpen(true)}
              className={`flex items-center gap-1 px-2 py-0.5 rounded-sm border text-xs hover:bg-ink transition-colors ${safetyCls}`}
              title="View InterQuest Safety v2 breakdown"
            >
              <SafetyIcon className="h-3 w-3" />
              <span className="font-display tracking-wider">{acc.safety_score.toFixed(1)}</span>
              <Info className="h-2.5 w-2.5 opacity-60" />
            </button>
          </div>
        </div>
        <p className="text-cream-dim text-xs leading-relaxed line-clamp-2">{acc.desc}</p>

        {/* Verified safety badges (NLP-derived) */}
        {(sv2?.badges?.verified_solo_female || sv2?.badges?.verified_for_minors) && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {sv2.badges.verified_solo_female && (
              <span
                data-testid={`badge-solo-female-${acc.id}`}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm bg-pink-500/15 border border-pink-400/50 text-pink-200"
                title={`Verified solo-female friendly. Keywords matched: ${(sv2.review_keywords || []).join(", ") || "—"}`}
              >
                <Heart className="h-3 w-3" /> Solo-female verified
              </span>
            )}
            {sv2.badges.verified_for_minors && (
              <span
                data-testid={`badge-minors-${acc.id}`}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm bg-sky-500/15 border border-sky-400/50 text-sky-200"
                title={`Verified for supervised minors. Keywords matched: ${(sv2.review_keywords || []).join(", ") || "—"}`}
              >
                <GraduationCap className="h-3 w-3" /> Verified for minors
              </span>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          {visibleTags.map((t) => (
            <span key={t} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-ink border border-panel-border text-cream-dim">
              {t}
            </span>
          ))}
          {acc.tag_labels && acc.tag_labels.length > 4 && (
            <span className="text-[10px] text-cream-dim/60">+{acc.tag_labels.length - 4}</span>
          )}
        </div>
        <div className="flex items-center justify-between mt-auto pt-2 border-t border-panel-border">
          <div className="flex items-center gap-3 text-[11px] text-cream-dim/80">
            <span className="inline-flex items-center gap-1"><Footprints className="h-3 w-3 text-gold" /> {acc.distance_min} min</span>
            {acc.capacity_groups > 0 && (
              <span className="inline-flex items-center gap-1"><Users className="h-3 w-3 text-gold" /> up to {acc.capacity_groups}</span>
            )}
          </div>
          <div className="text-right">
            <div className="font-display text-gold text-xl leading-none">€{acc.price_eur}</div>
            <div className="text-[10px] text-cream-dim/70 uppercase tracking-widest">per night</div>
            <button
              data-testid={`acc-add-${acc.id}`}
              onClick={() => {
                if (inCart) return;
                addItem({
                  id: acc.id,
                  kind: "accommodation",
                  name: acc.name,
                  sub: `${acc.type} · ${acc.slug}`,
                  price_eur: acc.price_eur,
                  qty: 1,
                  image: acc.image,
                });
                toast.success(`Added "${acc.name}" to cart`);
              }}
              className={`mt-1.5 inline-flex items-center gap-1 text-[10px] font-display tracking-wider uppercase px-2 py-1 rounded-sm transition-colors ${
                inCart ? "bg-emerald-400/15 text-emerald-400 border border-emerald-400/40" : "bg-gold text-ink hover:bg-cream"
              }`}
              disabled={inCart}
            >
              {inCart ? <><Check className="h-3 w-3" /> Added</> : <><ShoppingBag className="h-3 w-3" /> Add</>}
            </button>
          </div>
        </div>
      </div>

      {open && sv2 && (
        <div
          data-testid={`safety-modal-${acc.id}`}
          className="fixed inset-0 z-[60] bg-ink/85 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setOpen(false)}
        >
          <div
            className="iq-card max-w-lg w-full overflow-hidden animate-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-panel-border flex items-start justify-between gap-3">
              <div>
                <div className="iq-overline mb-1 flex items-center gap-1.5"><ShieldCheck className="h-3 w-3" /> InterQuest Safety v2</div>
                <h3 className="font-display text-cream text-2xl tracking-wider">{acc.name}</h3>
                <p className="text-cream-dim text-xs font-serif italic mt-0.5">Composite score blends six audited factors.</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-cream-dim hover:text-cream" aria-label="Close"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <div className="iq-overline mb-1">Composite</div>
                  <div className={`font-display text-5xl tracking-tight ${safetyColor(sv2.composite).split(" ")[0]}`}>
                    {sv2.composite.toFixed(1)}<span className="text-cream-dim/50 text-2xl">/10</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="iq-overline mb-1">City safety</div>
                  <div className="font-display text-3xl text-cream">{sv2.city_safety_index}<span className="text-cream-dim/50 text-base">/100</span></div>
                  <div className="text-[10px] text-cream-dim/70 uppercase tracking-widest">Numbeo-style</div>
                </div>
              </div>

              <div className="space-y-3">
                {Object.entries(sv2.breakdown).map(([k, v]) => (
                  <div key={k} data-testid={`sv2-${k}-${acc.id}`}>
                    <div className="flex items-baseline justify-between text-xs mb-1">
                      <span className="text-cream-dim">{v.label} <span className="text-cream-dim/50">· {v.weight}%</span></span>
                      <span className="font-display tracking-wider text-cream">{v.score.toFixed(1)}/10</span>
                    </div>
                    <div className="h-1.5 bg-ink rounded-full overflow-hidden">
                      <div
                        className={`h-full ${barColor(v.score)} transition-all`}
                        style={{ width: `${(v.score / 10) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-ink border border-panel-border rounded-sm p-3">
                <div className="iq-overline mb-1">Neighbourhood note</div>
                <p className="text-cream-dim text-xs leading-relaxed font-serif italic">{sv2.neighborhood_note}</p>
              </div>

              <p className="text-[10px] text-cream-dim/60 leading-relaxed">
                Methodology: city crime index (Numbeo-style public data), property security features (24/7 reception, CCTV, key-card access),
                solo-female friendliness, supervised-minor suitability, accessibility, and review-derived hygiene.
                <span className="block mt-1 text-cream-dim/40">Preview methodology — live partner-API data flows in at launch.</span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
